import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import FsLightbox from "fslightbox-react";
import {
  ContentTypeBadge,
  EmptyState,
  Page,
  StatusBadge,
} from "../../../components/ui";
import {
  AnalyticsPanel,
  CrewList,
  DetailHeader,
  DetailSkeleton,
  DetailTabs,
  FactList,
  MediaPlayer,
  OnOffBadge,
  TagList,
  useMediaAnalytics,
  useStoredDetail,
} from "../../../components/detail";
import { getMovieGC } from "../../../store/actions/uploadMediaAction";
import { getMovie } from "../../../services/content";
import CommentModeration from "../comments/CommentModeration";
import { formatDuration, getDate } from "../../../utils/format";
import { capitalize, stripHtml } from "../../../utils/list";
import { canEditContent, canViewAnalytics } from "../../../utils/roles";
import TrendingToggle from "./TrendingToggle";
import ScheduleRelease from "./ScheduleRelease";

const ViewMovie = () => {
  const { t } = useTranslation();
  const { videoInfo_id } = useParams();
  const allowedAdmins = canViewAnalytics();

  const { item: movie, loading } = useStoredDetail({
    load: () => getMovie(videoInfo_id),
    setInfo: getMovieGC,
    storeKey: "generalContentObj",
    pagePath: `/dashboard/video-info/${videoInfo_id}`,
  });
  const media = movie?.media;


  const analytics = useMediaAnalytics(
    [media?.media_id],
    movie?.createdAt,
    allowedAdmins
  );

  const [tab, setTab] = useState("review");
  const [trailerToggler, setTrailerToggler] = useState(false);

  const openTrailer = () => {
    if (movie?.trailer?.media_id) {
      setTrailerToggler(!trailerToggler);
    } else {
      toast.error("Trailer has not been uploaded yet!", {
        progressStyle: { background: "var(--iq-primary)" },
      });
    }
  };

  if (loading) {
    return (
      <Page>
        <DetailSkeleton />
      </Page>
    );
  }

  if (!movie) {
    return (
      <Page>
        <EmptyState icon="ri-film-line">
          {t("No data found for the provided ID!")}
        </EmptyState>
      </Page>
    );
  }

  const tabs = [
    ...(allowedAdmins
      ? [{ id: "description", label: t("Analytics"), icon: "ri-bar-chart-2-line" }]
      : []),
    { id: "review", label: t("Comments"), icon: "ri-chat-3-line" },
    { id: "crewmembers", label: capitalize(t("crew mem")), icon: "ri-group-line" },
  ];

  return (
    <Page>
      <FsLightbox
        toggler={trailerToggler}
        sources={[
          <iframe
            src={`https://cdn.jwplayer.com/players/${movie.trailer?.media_id}-4t00MwmP.html`}
            title={t("Watch trailer")}
            frameBorder="0"
            allow="autoplay; fullscreen"
            allowFullScreen
          />,
        ]}
      />

      <DetailHeader
        title={media?.title}
        badges={
          <>
            <StatusBadge active={Boolean(movie.status)} />
            <ContentTypeBadge type={movie.content_type} />
          </>
        }
        actions={
          <>
            {canEditContent("videos") && <ScheduleRelease video={movie} />}
            {canEditContent("videos") && <TrendingToggle video={movie} />}
            <button type="button" className="ui-btn" onClick={openTrailer}>
              <i className="ri-play-circle-line" aria-hidden="true"></i>
              {t("Watch trailer")}
            </button>
          </>
        }
      />

      <div className="detail-layout">
        <div className="detail-stack">
          <MediaPlayer
            mediaId={media?.media_id}
            emptyText={t("Video has not been uploaded yet")}
          />
          <section className="ui-card detail-pad">
            <div className="detail-section">
              <h3 className="detail-section-title">{t("desc")}</h3>
              <p className="detail-description">
                {stripHtml(media?.description) || "—"}
              </p>
            </div>
            <TagList
              label={t("Genre")}
              items={(movie.genre || []).map((genre) => genre.name)}
            />
            <TagList
              label={t("tags")}
              items={(media?.jw_tags || []).filter(
                (tag) => !tag.endsWith("-movie")
              )}
            />
            <TagList label={t("Seo Keywords")} items={media?.seo_tags} />
          </section>
        </div>

        <section className="ui-card detail-pad">
          {movie.thumbnail?.static_thumbnail_url && (
            <img
              className="detail-poster"
              src={movie.thumbnail.static_thumbnail_url}
              alt=""
            />
          )}
          <FactList
            items={[
              { label: t("availability"), value: movie.availability },
              {
                label: t("Duration"),
                value: formatDuration(media?.duration),
              },
              {
                label: t("Release Date"),
                value: getDate(media?.release_year),
              },
              { label: t("Default Language"), value: media?.default_language },
              {
                label: t("Monetization"),
                value: <OnOffBadge on={Boolean(media?.monetization)} />,
              },
            ]}
          />
        </section>
      </div>

      <section className="ui-card">
        <DetailTabs tabs={tabs} value={tab} onChange={setTab} />
        <div className="detail-tab-panel">
          {allowedAdmins && tab === "description" && (
            <AnalyticsPanel
              {...analytics}
              emptyText={t("Video has not been uploaded yet")}
            />
          )}
          {tab === "review" && <CommentModeration contentType="video" contentId={movie?._id} />}
          {tab === "crewmembers" && <CrewList members={movie.crew_members} />}
        </div>
      </section>
    </Page>
  );
};

export default React.memo(ViewMovie);
